import type { FC, MouseEventHandler, PropsWithChildren } from "react";

import styled from "styled-components";

import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { IconButton } from "@similarweb/ui-components";

import HighlightTerm from "app/components/highlight-term/HighlightTerm";
import { translate } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import type { Keyword } from "../types";

import { Checkbox } from "./Checkbox";
import { Volume } from "./Volume";

const { blue, carbon } = colorsPalettes;

interface KeywordItemProps {
    onRemoveKeyword: VoidFunction;
    onTermClick: VoidFunction;
    Container: React.ComponentType<
        PropsWithChildren<{
            $isHovered: boolean;
            $pointerEvents?: "none" | "all";
            onClick?: MouseEventHandler<HTMLDivElement>;
        }>
    >;
    DisplayedOnHover: FC<PropsWithChildren>;
    keyword: Keyword;
    isHovered?: boolean;
    isSeedKeyword?: boolean;
    query?: string;
    selected?: boolean;
}

const ContentContainer = styled(FlexRow)<{ clickable?: boolean }>`
    gap: 8px;
    align-items: center;
    ${({ clickable }) => clickable && "cursor: pointer;"}
`;

const KeywordIndicator = styled.div<{ color?: string; backgroundColor?: string }>`
    padding: 4px 8px;
    ${({ color = blue[400] }) => setFontVariant("tag", color)}
    background-color: ${({ backgroundColor = blue[100] }) => backgroundColor};
    border-radius: 16px;
    letter-spacing: 0.6px;
`;

export const KeywordItem: FC<KeywordItemProps> = ({
    isHovered = false,
    onRemoveKeyword,
    onTermClick,
    Container,
    DisplayedOnHover,
    keyword,
    isSeedKeyword,
    query,
}) => {
    const { term, selected, isRecentlyAdded } = keyword;
    const onClick: MouseEventHandler<HTMLDivElement> = (e) => {
        onRemoveKeyword();
        const customAttributes = {
            deletionType: "delete single keyword",
        };
        allTrackers.dispatchTrackEvent("Pop Up", "click", "delete", customAttributes);
        e.stopPropagation();
    };
    return (
        <Container
            $isHovered={isHovered}
            onClick={onTermClick}
        >
            <ContentContainer clickable={true}>
                {selected ? (
                    <Checkbox selected={selected} />
                ) : (
                    <DisplayedOnHover>
                        <Checkbox selected={selected} />
                    </DisplayedOnHover>
                )}

                {query ? (
                    <HighlightTerm
                        sentence={term}
                        highlightTerm={query}
                    />
                ) : (
                    <span>{term}</span>
                )}
                {isSeedKeyword ? (
                    <KeywordIndicator>
                        {translate("auto.keyword.list.generator.body.keywords.seed.indicator")}
                    </KeywordIndicator>
                ) : null}
                {isRecentlyAdded ? (
                    <KeywordIndicator
                        backgroundColor={carbon[300]}
                        color={carbon[0]}
                    >
                        {translate("auto.keyword.list.generator.body.keywords.recently.indicator")}
                    </KeywordIndicator>
                ) : null}
            </ContentContainer>
            <ContentContainer>
                <Volume keyword={keyword} />
                <DisplayedOnHover>
                    <IconButton
                        type="flat"
                        iconSize="xs"
                        iconName="delete"
                        onClick={onClick}
                    />
                </DisplayedOnHover>
            </ContentContainer>
        </Container>
    );
};
